import { useNavigate } from "react-router-dom";
import { PriorityDot } from "@/components/shared/PriorityDot";
import { BusinessStage } from "@/components/shared/BusinessStage";
import { useRole } from "@/api/hooks";
import { ChevronRight, Check } from "lucide-react";

export function ActionInbox({ items }) {
  const navigate = useNavigate();
  const role = useRole();

  const routeFor = (link) => {
    if (!link?.type || !link?.id) return null;
    switch (link.type) {
      case "lead": return role === "sales" ? `/sales/leads/${link.id}` : null;
      // Aug 2026 IA correction: an Inquiry never opens as its own page —
      // when the backend gives us the parent clientId (see /actions/sales
      // in server.py), route straight into that customer's page with the
      // inquiry pre-selected. Only fall back to the redirect route when an
      // older/incomplete payload lacks clientId.
      case "inquiry": return role === "sales" ? (link.clientId ? `/sales/customers/${link.clientId}?inquiry=${link.id}` : `/sales/inquiries/${link.id}`) : null;
      case "customer": return ["sales", "operations", "finance", "management"].includes(role) ? `/${role}/customers/${link.id}` : null;
      case "quotation": return role === "sales" ? `/sales/quotations/${link.id}` : null;
      case "job": return ["sales", "operations", "finance", "management"].includes(role) ? `/${role}/jobs/${link.id}` : null;
      case "credit": return role === "finance" ? "/finance/credit" : null;
      case "ap": return role === "finance" ? "/finance/payables" : null;
      case "ar": case "invoice": return role === "finance" ? "/finance/receivables" : null;
      case "approval": return ["finance", "management"].includes(role) ? `/${role}/approvals` : null;
      case "exception": return ["operations", "finance", "management"].includes(role) ? `/${role}/exceptions` : null;
      // Phase 4.5 — real Finance Gate / Operations Handoff work items.
      case "acceptance": return role === "finance" ? `/finance/orders/${link.id}` : null;
      case "operations_handoff": return role === "operations" ? `/operations/handoffs/${link.id}` : null;
      default: return null;
    }
  };

  return (
    <div className="divide-y divide-slate-100">
      {items.map((item) => {
        const route = routeFor(item.link);
        return (
        <button key={item.id} data-testid={`action-${item.id}`} onClick={() => route && navigate(route)} disabled={!route}
          className={`group w-full text-left px-6 py-3.5 flex items-start gap-3 transition-colors ${route ? "hover:bg-slate-50" : "cursor-default"} ${item.done ? "opacity-55" : ""}`}>
          <div className="pt-1">
            {item.done ? (
              <span className="h-4 w-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="h-2.5 w-2.5 text-emerald-600" strokeWidth={3} />
              </span>
            ) : <PriorityDot priority={item.priority} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold text-slate-800 ${item.done ? "line-through decoration-slate-300" : ""}`}>{item.title}</span>
              <span className="text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 shrink-0">{item.tag}</span>
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">{item.why}</div>
            {item.businessStage && (
              <div className="mt-2 flex min-w-0 items-center gap-2">
                <BusinessStage stage={item.businessStage} compact />
                <span className="truncate text-[11px] text-slate-500">{item.businessStage.name} · Last status: {item.businessStage.lastStatus}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[11px] text-slate-400 font-mono-num">{item.due}</span>
            {route && <span className="hidden group-hover:flex items-center gap-1 text-xs font-semibold text-indigo-600">{item.action} <ChevronRight className="h-3.5 w-3.5" /></span>}
          </div>
        </button>
        );
      })}
    </div>
  );
}
